import { HeaderActionsContext } from '@/components/HeaderActions';
import { createPortal } from 'react-dom';
import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Typography, Space, Empty, Button, Breadcrumb, Dropdown, Input, Segmented, Tooltip } from 'antd';
import { Reply, MoreVertical } from 'lucide-react';
import { useTheme } from '@/App';
import { useResponsive } from '@/hooks/useResponsive';
import { usePageChrome } from '@/components/PageChrome';
import { defaultColors } from '@/themes/antd-theme';

/**
 * PageHeader — the one page-header pattern for the app.
 *
 * LEFT  = identity / context:   icon · title · subtitle ("updated 0 min ago") · meta chips · description
 * RIGHT = controls, always in this order:
 *         filters → search → view switch → secondary actions → extra → primary action → back → "⋮" menu (always last)
 *
 *   <PageHeader
 *     icon={<Icons.FileText size={22} />}
 *     title="Records"
 *     subtitle="updated 0 min ago"                              // muted text right after the title
 *     breadcrumbs={[{ title: 'Library', to: '/library' }, { title: 'Records' }]}
 *     meta={<Tag color="gold">Pending</Tag>}                    // chips beside the title
 *     description="One line under the title."                   // optional
 *     filters={<Segmented … />}                                 // header-level filters (node)
 *     search={{ value, onChange, onSearch, placeholder, width }} // onChange gets the DOM event, like Input.Search
 *     view={{ value, onChange, options: ['Cards', 'Table'] }}
 *     secondary={[{ key: 'refresh', label: 'Refresh', icon: <ReloadOutlined />, onClick, loading, disabled, hidden, tooltip }]}
 *     primary={{ label: 'Add', icon: <PlusOutlined />, onClick, disabled, hidden, loading, tooltip }}
 *     more={[{ key: 'x', label: 'Export', icon, onClick }]}     // antd Dropdown menu items
 *     back={false}                                              // Back is ON by default on every header; false hides it (e.g. a header inside a tab or panel). true = history back (same as the app bar's return icon); a path string or a function also works; sits just before the ⋮ menu
 *     extra={<anything/>}                                       // escape hatch: lands in the right group before the primary action
 *   />
 *
 * Responsive: below `lg` the secondary actions fold into the "…" menu, the primary action drops
 * its label (keeps its icon + tooltip), and on phones the controls wrap under the title with a
 * full-width search. Every prop is optional, so the older `<PageHeader icon title description extra />`
 * call sites render exactly as before.
 *
 * Use it as the `title` of a Card/MainCard (pass `compact`) or on its own above a DataTable.
 * `compact` drops the bottom margin for use inside Card `title` slots.
 * `surface` gives the header its own white bordered card; leave it off when the header is the
 * title of the page's Card (the normal case — put the whole page inside one Card).
 */

const SEARCH_WIDTH = 240;

// The icon is always the one header blue, whatever colour or class the page put on it.
// Segmented centres text labels but not icon-only ones (the icon rides high in its box); this
// centres both, and keeps every header control — switch, select, search, button — on the theme's 36px control height.
const HEADER_CSS = `
.ph-icon, .ph-icon svg { color: #2563eb !important; }
.ph-icon svg { stroke: currentColor !important; }
/* two-line header (title + description): the icon grows to span both lines; a one-line header keeps the 22px icon centred on the title */
.ph-icon-lg svg { width: 40px !important; height: 40px !important; }
.ph-icon-lg { margin-right: 6px; }
.ph-controls .ant-segmented-item-label { display: flex; align-items: center; justify-content: center; min-height: 32px; line-height: 1; padding: 0 12px; }
.ph-controls .ant-segmented { padding: 2px; }
.ph-controls .ant-btn, .ph-controls .ant-select .ant-select-selector, .ph-controls .ant-input-affix-wrapper, .ph-controls .ant-input-search .ant-input-search-button { min-height: 36px; }
.ph-controls .ant-segmented-item-icon { display: flex; align-items: center; justify-content: center; margin: 0; }
.ph-controls .ant-segmented-item-icon + * { margin-inline-start: 6px; }
`;

const asMenuItem = (a) => ({ key: a.key, label: a.label, icon: a.icon, onClick: a.onClick, disabled: a.disabled || a.loading, danger: a.danger });

function HeaderButton({ action, type, iconOnly }) {
  if (!action || action.hidden) return null;
  const { key, label, icon, onClick, disabled, loading, tooltip, danger, testId } = action;
  const hideLabel = iconOnly && icon;
  const btn = (
    <Button
      className="!inline-flex !items-center !justify-center"
      type={type}
      danger={danger}
      icon={icon}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      aria-label={typeof label === 'string' ? label : undefined}
      data-testid={testId}
    >
      {hideLabel ? null : label}
    </Button>
  );
  const tip = tooltip || (hideLabel && typeof label === 'string' ? label : null);
  return tip ? <Tooltip key={key} title={tip}>{btn}</Tooltip> : btn;
}

export default function PageHeader({
  icon,
  title,
  subtitle,
  description,
  breadcrumbs,
  meta,
  filters,
  search,
  view,
  secondary,
  primary,
  more,
  back = true,
  extra,
  compact = false,
  surface = false,
  className = '',
  style,
}) {
  const { isDark } = useTheme();
  const { isMobile, isDesktop } = useResponsive();
  const navigate = useNavigate();
  const { returnTo } = usePageChrome();   // a page that declared usePageReturn(...) keeps its own target
  const slot = React.useContext(HeaderActionsContext);   // set when a host shows this page's controls

  const titleColor = isDark ? '#f1f5f9' : '#1f2937';
  const descColor = isDark ? '#94a3b8' : '#64748b';

  const shownSecondary = (secondary || []).filter((a) => a && !a.hidden);
  const foldSecondary = !isDesktop && shownSecondary.length > 0;
  const menuItems = [
    ...(foldSecondary ? shownSecondary.map(asMenuItem) : []),
    ...(foldSecondary && more?.length ? [{ type: 'divider', key: '__divider' }] : []),
    ...(more || []),
  ];

  // `back`: true → where the page declared (usePageReturn), else history back (a deep link opened in a
  // fresh tab has none, so go to the root). A page that declared usePageReturn(null) shows no Back.
  const showBack = back && !(back === true && (returnTo === null || returnTo === false));
  const onBack = () => {
    if (typeof back === 'function') return back();
    if (typeof back === 'string') return navigate(back);
    if (typeof returnTo === 'function') return returnTo();
    if (returnTo && typeof returnTo === 'object' && returnTo.to !== undefined) return navigate(returnTo.to, { state: returnTo.state, replace: returnTo.replace });
    if (typeof returnTo === 'string') return navigate(returnTo);
    if (typeof returnTo === 'number' && returnTo !== -1) return navigate(returnTo);
    return (window.history.state?.idx ?? 0) === 0 ? navigate('/', { replace: true }) : navigate(-1);
  };

  const hasRight = !!(showBack || filters || search || view || shownSecondary.length || extra || (primary && !primary.hidden) || menuItems.length);

  const controls = (
          <div className="ph-controls flex items-center gap-2.5 flex-wrap justify-end ml-auto" style={isMobile ? { width: '100%', justifyContent: 'flex-start' } : undefined}>
            {filters}
            {search && (
              <Input.Search
                allowClear
                value={search.value}
                onChange={search.onChange}
                onSearch={search.onSearch}
                placeholder={search.placeholder || 'Search...'}
                style={{ width: isMobile ? '100%' : search.width || SEARCH_WIDTH }}
                data-testid={search.testId}
              />
            )}
            {view && <Segmented value={view.value} onChange={view.onChange} options={view.options} />}
            {!foldSecondary && shownSecondary.map((a) => <HeaderButton key={a.key} action={a} type="default" />)}
            {extra && <Space size={8} wrap>{extra}</Space>}
            <HeaderButton action={primary} type="primary" iconOnly={isMobile} />
            {showBack && (
              <Tooltip title="Back">
                <Button icon={<Reply size={18} strokeWidth={2.4} />} onClick={onBack} aria-label="Back" data-testid="page-header-back" className="!inline-flex !items-center !justify-center !p-0" style={{ width: 36, height: 36 }} />
              </Tooltip>
            )}
            {menuItems.length > 0 && (
              <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: menuItems }}>
                <Button type="text" aria-label="More actions" icon={<MoreVertical size={18} />} data-testid="page-header-more" className="!inline-flex !items-center !justify-center !p-0" style={{ width: 36, height: 36 }} />
              </Dropdown>
            )}
          </div>
  );

  // Hosted inside a page with its own header (Recipe Building Blocks): only the controls, sent
  // to the host's slot — the title would repeat the tab the host already shows selected.
  if (slot) return hasRight ? createPortal(<div className="ph-hosted">{controls}</div>, slot) : null;

  return (
    <div
      className={className}
      style={{
        marginBottom: compact ? 0 : 12,
        // a little breathing room above and below, so the header reads as a bar rather than a line of text
        ...(compact ? { paddingTop: 8, paddingBottom: 8 } : {}),
        ...(surface ? {
          background: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          border: `1px solid ${isDark ? defaultColors.palette.gray[700] : '#d1d5db'}`,
          borderRadius: 8,
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
          padding: '16px 20px',
        } : {}),
        ...style,
      }}
      data-testid="page-header"
    >
      <style>{HEADER_CSS}</style>
      {breadcrumbs?.length > 0 && (
        <Breadcrumb
          style={{ marginBottom: 4, fontSize: 12 }}
          items={breadcrumbs.map((b) => ({ key: b.key || String(b.title), title: b.to ? <Link to={b.to}>{b.title}</Link> : b.title }))}
        />
      )}
      <div className="flex items-center justify-between gap-x-4 gap-y-2 flex-wrap" style={{ minHeight: 40 }}>
        {/* LEFT — identity */}
        <div className="ph-title flex items-center gap-2 min-w-0">
          {icon && <span className={`ph-icon${description ? ' ph-icon-lg' : ''} flex items-center justify-center text-blue-600 shrink-0`} style={{ lineHeight: 0 }}>{icon}</span>}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Typography.Title level={4} style={{ margin: 0, color: titleColor, lineHeight: 1.25, fontWeight: 600 }}>
                {title}
              </Typography.Title>
              {subtitle && <Typography.Text style={{ color: descColor, fontSize: 12, fontWeight: 500, lineHeight: 1.25 }}>{subtitle}</Typography.Text>}
              {meta && <span className="flex items-center gap-1">{meta}</span>}
            </div>
            {description && (
              <Typography.Text style={{ color: descColor, fontSize: 13, fontWeight: 400, display: 'block' }}>
                {description}
              </Typography.Text>
            )}
          </div>
        </div>

        {/* RIGHT — controls */}
        {hasRight && controls}
      </div>
    </div>
  );
}

const actionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.node,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  hidden: PropTypes.bool,
  danger: PropTypes.bool,
  tooltip: PropTypes.node,
  testId: PropTypes.string,
});

PageHeader.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  description: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.node.isRequired, to: PropTypes.string, key: PropTypes.string })),
  meta: PropTypes.node,
  filters: PropTypes.node,
  search: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func,
    onSearch: PropTypes.func,
    placeholder: PropTypes.string,
    width: PropTypes.number,
    testId: PropTypes.string,
  }),
  view: PropTypes.shape({ value: PropTypes.any, onChange: PropTypes.func, options: PropTypes.array.isRequired }),
  secondary: PropTypes.arrayOf(actionShape),
  primary: actionShape,
  more: PropTypes.array,
  back: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.func]),
  extra: PropTypes.node,
  compact: PropTypes.bool,
  surface: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * EmptyState — empty table / not-configured placeholder with a call to action.
 *
 *   <DataTable locale={{ emptyText: <EmptyState title="No products yet" description="Add your first product to get started." actionLabel="Add Product" onAction={openForm} /> }} … />
 *   <EmptyState variant="unavailable" title="Clearance Types are disabled" description="…" />
 */
export function EmptyState({ title = 'No data', description, actionLabel, onAction, action, icon, variant = 'empty', style }) {
  const { isDark } = useTheme();
  const descColor = isDark ? '#94a3b8' : '#64748b';
  return (
    <Empty
      image={variant === 'unavailable' ? Empty.PRESENTED_IMAGE_SIMPLE : Empty.PRESENTED_IMAGE_DEFAULT}
      imageStyle={{ height: variant === 'unavailable' ? 48 : 72 }}
      style={{ padding: '32px 16px', ...style }}
      description={
        <div className="flex flex-col items-center gap-1">
          {icon && <span className="text-blue-600 mb-1">{icon}</span>}
          <Typography.Text strong style={{ color: isDark ? '#e2e8f0' : '#1f2937' }}>{title}</Typography.Text>
          {description && <Typography.Text style={{ color: descColor, fontSize: 13, maxWidth: 480 }}>{description}</Typography.Text>}
        </div>
      }
    >
      {action || (actionLabel && onAction ? <Button type="primary" onClick={onAction}>{actionLabel}</Button> : null)}
    </Empty>
  );
}

EmptyState.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  actionLabel: PropTypes.node,
  onAction: PropTypes.func,
  action: PropTypes.node,
  icon: PropTypes.node,
  variant: PropTypes.oneOf(['empty', 'unavailable']),
  style: PropTypes.object,
};
